import { SEO_CONFIG, createOrganizationSchema, getCanonicalUrl } from './seo'

/**
 * Creates Article schema for blog posts and articles
 */
export const createArticleSchema = (article: {
  title: string
  description: string
  author?: string
  publishedAt?: string
  updatedAt?: string
  image?: string
  category?: string
}, canonicalUrl?: string) => {
  if (!article) {
    throw new Error('Article data is required')
  }
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Organization',
      name: article.author || SEO_CONFIG.siteName
    },
    publisher: createOrganizationSchema(),
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    image: article.image,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl || getCanonicalUrl()
    },
    keywords: article.category
  }
}

/**
 * Creates Service schema for service pages
 */
export const createServiceSchema = (service: {
  name: string
  description: string
}) => {
  if (!service) {
    throw new Error('Service data is required')
  }
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: createOrganizationSchema(),
    areaServed: {
      '@type': 'Country',
      name: 'Australia'
    },
    serviceType: 'Financial Advisory Service'
  }
}

/**
 * Creates WebSite schema with search functionality
 */
export const createWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SEO_CONFIG.siteName,
  url: SEO_CONFIG.domain,
  publisher: createOrganizationSchema(),
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SEO_CONFIG.domain}/learn/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string'
  }
})

/**
 * Creates ContactPage schema
 */
export const createContactPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Otivo',
  description: 'Get in touch with Otivo for financial advice and support',
  mainEntity: createOrganizationSchema()
})

/**
 * Creates FAQPage schema (when you have FAQ structured data)
 */
export const createFAQPageSchema = (faqs: Array<{ question: string; answer: string }>) => {
  if (!faqs) {
    throw new Error('FAQ data is required')
  }
  
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }
}